import { Permission } from '../types';

export type { Permission };

export interface PermissionGroup {
  id: string;
  label: string;
  description: string;
  permissions: Permission[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'users',
    label: 'User Management',
    description: 'Manage user accounts and access',
    permissions: [
      'users:read',
      'users:create',
      'users:update',
      'users:delete',
      'users:manage-devices',
    ],
  },
  {
    id: 'courses',
    label: 'Course Management',
    description: 'Manage courses, modules, and content',
    permissions: [
      'courses:read',
      'courses:create',
      'courses:update',
      'courses:delete',
      'courses:publish',
      'courses:manage-content',
    ],
  },
  {
    id: 'enrollments',
    label: 'Enrollment Management',
    description: 'Manage student enrollments and subscriptions',
    permissions: [
      'enrollments:read',
      'enrollments:create',
      'enrollments:update',
      'enrollments:delete',
      'enrollments:suspend',
      'enrollments:bulk-actions',
    ],
  },
  {
    id: 'instructors',
    label: 'Instructor Management',
    description: 'Manage instructor accounts and applications',
    permissions: [
      'instructors:read',
      'instructors:create',
      'instructors:update',
      'instructors:delete',
      'instructors:approve',
    ],
  },
  {
    id: 'trades',
    label: 'Trades Management',
    description: 'Manage trade categories and statistics',
    permissions: [
      'trades:read',
      'trades:create',
      'trades:update',
      'trades:delete',
      'trades:toggle-active',
      'trades:view-stats',
    ],
  },
  {
    id: 'coupons',
    label: 'Coupons Management',
    description: 'Manage discount coupons and promotions',
    permissions: [
      'coupons:read',
      'coupons:create',
      'coupons:update',
      'coupons:delete',
      'coupons:toggle-status',
      'coupons:view-usage',
    ],
  },
  {
    id: 'payments',
    label: 'Payments Management',
    description: 'Manage payment records and transactions',
    permissions: [
      'payments:read',
      'payments:create',
      'payments:update-status',
      'payments:delete',
      'payments:view-by-user',
      'payments:view-by-date',
    ],
  },
  {
    id: 'reviews',
    label: 'Reviews Management',
    description: 'Moderate and manage course reviews',
    permissions: [
      'reviews:read',
      'reviews:moderate',
      'reviews:delete',
    ],
  },
  {
    id: 'blogs',
    label: 'Blogs Management',
    description: 'Manage blog posts and articles',
    permissions: [
      'blogs:read',
      'blogs:create',
      'blogs:update',
      'blogs:delete',
    ],
  },
  {
    id: 'certificates',
    label: 'Certificates Management',
    description: 'Manage certificate requests and issuance',
    permissions: [
      'certificates:read-requests',
      'certificates:approve',
      'certificates:reject',
      'certificates:revoke',
    ],
  },
  {
    id: 'quizzes',
    label: 'Quizzes Management',
    description: 'Manage quizzes and exam questions',
    permissions: [
      'quizzes:read',
      'quizzes:create',
      'quizzes:update',
      'quizzes:delete',
      'quizzes:publish',
      'quizzes:view-stats',
      'quizzes:sync',
      'quizzes:manage-questions',
    ],
  },
  {
    id: 'packages',
    label: 'Packages Management',
    description: 'Manage course packages and bundles',
    permissions: [
      'packages:read',
      'packages:create',
      'packages:update',
      'packages:delete',
      'packages:view-stats',
    ],
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions Management',
    description: 'Manage subscription plans',
    permissions: [
      'subscriptions:read',
      'subscriptions:create',
      'subscriptions:update',
      'subscriptions:delete',
    ],
  },
  {
    id: 'content-settings',
    label: 'Content Settings',
    description: 'Manage general content and settings',
    permissions: [
      'settings:read',
      'settings:update',
      'content:update-general',
      'content:manage-faq',
      'content:manage-slider',
      'content:manage-testimonials',
      'content:toggle-maintenance',
      'content:view-analytics',
    ],
  },
  {
    id: 'social-links',
    label: 'Social Links Management',
    description: 'Manage social media links',
    permissions: [
      'social-links:read',
      'social-links:create',
      'social-links:update',
      'social-links:delete',
    ],
  },
  {
    id: 'over-usage',
    label: 'Over Usage Monitoring',
    description: 'Monitor and manage suspicious usage',
    permissions: [
      'over-usage:read',
      'over-usage:review',
      'over-usage:resolve',
      'over-usage:bulk-check',
    ],
  },
  {
    id: 'videos',
    label: 'Video Management',
    description: 'Monitor video access and activities',
    permissions: [
      'videos:view-logs',
      'videos:view-suspicious',
    ],
  },
  {
    id: 'uploads',
    label: 'Uploads Management',
    description: 'Manage file uploads',
    permissions: [
      'upload:create',
      'upload:delete',
    ],
  },
  {
    id: 'campaigns',
    label: 'Email Campaigns',
    description: 'Manage email marketing campaigns',
    permissions: [
      'campaigns:read',
      'campaigns:create',
      'campaigns:update',
      'campaigns:delete',
      'campaigns:send',
      'campaigns:schedule',
      'campaigns:cancel',
      'campaigns:view-analytics',
    ],
  },
  {
    id: 'devices',
    label: 'Device Management',
    description: 'Manage user devices across platform',
    permissions: [
      'devices:read-all',
      'devices:block',
      'devices:unblock',
      'devices:reset-all',
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics Management',
    description: 'View and export analytics data',
    permissions: [
      'analytics:read',
      'analytics:export',
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications Management',
    description: 'Manage system notifications',
    permissions: [
      'notifications:read',
      'notifications:manage',
    ],
  },
  {
    id: 'discussions',
    label: 'Discussions Management',
    description: 'Manage forum discussions',
    permissions: [
      'discussions:read',
      'discussions:moderate',
      'discussions:delete',
    ],
  },
  {
    id: 'admin',
    label: 'Admin Management',
    description: 'Manage other admin users (Super Admin only)',
    permissions: [
      'admin:read',
      'admin:create',
      'admin:update-permissions',
      'admin:delete',
    ],
  },
];

export const PERMISSION_LABELS: Record<string, string> = {
  'users:read': 'View Users',
  'users:create': 'Create Users',
  'users:update': 'Update Users',
  'users:delete': 'Delete Users',
  'users:manage-devices': 'Manage User Devices',

  'courses:read': 'View Courses',
  'courses:create': 'Create Courses',
  'courses:update': 'Update Courses',
  'courses:delete': 'Delete Courses',
  'courses:publish': 'Publish Courses',
  'courses:manage-content': 'Manage Course Content',

  'enrollments:read': 'View Enrollments',
  'enrollments:create': 'Create Enrollments',
  'enrollments:update': 'Update Enrollments',
  'enrollments:delete': 'Delete Enrollments',
  'enrollments:suspend': 'Suspend Enrollments',
  'enrollments:bulk-actions': 'Bulk Enrollment Actions',

  'instructors:read': 'View Instructors',
  'instructors:create': 'Create Instructors',
  'instructors:update': 'Update Instructors',
  'instructors:delete': 'Delete Instructors',
  'instructors:approve': 'Approve Instructors',

  'trades:read': 'View Trades',
  'trades:create': 'Create Trades',
  'trades:update': 'Update Trades',
  'trades:delete': 'Delete Trades',
  'trades:toggle-active': 'Toggle Trade Status',
  'trades:view-stats': 'View Trade Statistics',

  'coupons:read': 'View Coupons',
  'coupons:create': 'Create Coupons',
  'coupons:update': 'Update Coupons',
  'coupons:delete': 'Delete Coupons',
  'coupons:toggle-status': 'Toggle Coupon Status',
  'coupons:view-usage': 'View Coupon Usage',

  'payments:read': 'View Payments',
  'payments:create': 'Create Manual Payment',
  'payments:update-status': 'Update Payment Status',
  'payments:delete': 'Delete Payment',
  'payments:view-by-user': 'View User Payments',
  'payments:view-by-date': 'View Payments by Date',

  'reviews:read': 'View Reviews',
  'reviews:moderate': 'Moderate Reviews',
  'reviews:delete': 'Delete Reviews',

  'blogs:read': 'View Blogs',
  'blogs:create': 'Create Blogs',
  'blogs:update': 'Update Blogs',
  'blogs:delete': 'Delete Blogs',

  'certificates:read-requests': 'View Certificate Requests',
  'certificates:approve': 'Approve Certificates',
  'certificates:reject': 'Reject Certificates',
  'certificates:revoke': 'Revoke Certificates',

  'quizzes:read': 'View Quizzes',
  'quizzes:create': 'Create Quizzes',
  'quizzes:update': 'Update Quizzes',
  'quizzes:delete': 'Delete Quizzes',
  'quizzes:publish': 'Publish Quizzes',
  'quizzes:view-stats': 'View Quiz Statistics',
  'quizzes:sync': 'Sync Quizzes',
  'quizzes:manage-questions': 'Manage Quiz Questions',

  'packages:read': 'View Packages',
  'packages:create': 'Create Packages',
  'packages:update': 'Update Packages',
  'packages:delete': 'Delete Packages',
  'packages:view-stats': 'View Package Statistics',

  'subscriptions:read': 'View Subscriptions',
  'subscriptions:create': 'Create Subscriptions',
  'subscriptions:update': 'Update Subscriptions',
  'subscriptions:delete': 'Delete Subscriptions',

  'settings:read': 'View Settings',
  'settings:update': 'Update Settings',
  'content:update-general': 'Update General Content',
  'content:manage-faq': 'Manage FAQs',
  'content:manage-slider': 'Manage Slider',
  'content:manage-testimonials': 'Manage Testimonials',
  'content:toggle-maintenance': 'Toggle Maintenance Mode',
  'content:view-analytics': 'View Content Analytics',

  'social-links:read': 'View Social Links',
  'social-links:create': 'Create Social Links',
  'social-links:update': 'Update Social Links',
  'social-links:delete': 'Delete Social Links',

  'over-usage:read': 'View Usage Statistics',
  'over-usage:review': 'Review Suspicious Enrollments',
  'over-usage:resolve': 'Resolve Usage Alerts',
  'over-usage:bulk-check': 'Run Bulk Checks',

  'videos:view-logs': 'View Video Logs',
  'videos:view-suspicious': 'View Suspicious Activities',

  'upload:create': 'Upload Files',
  'upload:delete': 'Delete Files',

  'campaigns:read': 'View Campaigns',
  'campaigns:create': 'Create Campaigns',
  'campaigns:update': 'Update Campaigns',
  'campaigns:delete': 'Delete Campaigns',
  'campaigns:send': 'Send Campaigns',
  'campaigns:schedule': 'Schedule Campaigns',
  'campaigns:cancel': 'Cancel Campaigns',
  'campaigns:view-analytics': 'View Campaign Analytics',

  'devices:read-all': 'View All Devices',
  'devices:block': 'Block Devices',
  'devices:unblock': 'Unblock Devices',
  'devices:reset-all': 'Reset All Devices',

  'analytics:read': 'View Analytics',
  'analytics:export': 'Export Analytics',

  'notifications:read': 'View Notifications',
  'notifications:manage': 'Manage Notifications',

  'discussions:read': 'View Discussions',
  'discussions:moderate': 'Moderate Discussions',
  'discussions:delete': 'Delete Discussions',

  'admin:read': 'View Admins',
  'admin:create': 'Create Admins',
  'admin:update-permissions': 'Update Admin Permissions',
  'admin:delete': 'Delete Admins',
};

export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  'users:read': 'Can view the user list and user details',
  'users:create': 'Can create new user accounts',
  'users:update': 'Can update user information',
  'users:delete': 'Can delete user accounts',
  'users:manage-devices': 'Can manage user devices and sessions',

  'courses:read': 'Can view the course list and course details',
  'courses:create': 'Can create new courses',
  'courses:update': 'Can edit course details',
  'courses:delete': 'Can delete courses',
  'courses:publish': 'Can publish and unpublish courses',
  'courses:manage-content': 'Can manage course modules and lessons',

  'enrollments:read': 'Can view enrollments list',
  'enrollments:create': 'Can create new enrollments',
  'enrollments:update': 'Can update enrollment details',
  'enrollments:delete': 'Can delete enrollments',
  'enrollments:suspend': 'Can suspend and reinstate enrollments',
  'enrollments:bulk-actions': 'Can perform bulk operations on enrollments',

  'instructors:read': 'Can view instructor list and details',
  'instructors:create': 'Can create new instructor accounts',
  'instructors:update': 'Can update instructor profiles',
  'instructors:delete': 'Can delete instructor accounts',
  'instructors:approve': 'Can approve or reject instructor applications',

  'trades:read': 'Can view trades and categories',
  'trades:create': 'Can create new trades',
  'trades:update': 'Can update existing trades',
  'trades:delete': 'Can delete trades',
  'trades:toggle-active': 'Can toggle trade active status',
  'trades:view-stats': 'Can view trade statistics',

  'coupons:read': 'Can view all coupons and stats',
  'coupons:create': 'Can create new coupons',
  'coupons:update': 'Can update coupon details',
  'coupons:delete': 'Can delete coupons',
  'coupons:toggle-status': 'Can activate/deactivate coupons',
  'coupons:view-usage': 'Can view coupon usage history',

  'payments:read': 'Can view all payments and stats',
  'payments:create': 'Can create manual payment records',
  'payments:update-status': 'Can update payment status',
  'payments:delete': 'Can delete payment records',
  'payments:view-by-user': 'Can view user payment history',
  'payments:view-by-date': 'Can view payments by date range',

  'reviews:read': 'Can view all reviews (admin view)',
  'reviews:moderate': 'Can flag/unflag reviews',
  'reviews:delete': 'Can delete inappropriate reviews',

  'blogs:read': 'Can view all blogs (admin view)',
  'blogs:create': 'Can create new blog posts',
  'blogs:update': 'Can update blog posts',
  'blogs:delete': 'Can delete blogs',

  'certificates:read-requests': 'Can view certificate requests',
  'certificates:approve': 'Can approve certificate requests',
  'certificates:reject': 'Can reject certificate requests',
  'certificates:revoke': 'Can revoke issued certificates',

  'quizzes:read': 'Can view all quizzes (admin view)',
  'quizzes:create': 'Can create new quizzes',
  'quizzes:update': 'Can update quizzes',
  'quizzes:delete': 'Can delete quizzes',
  'quizzes:publish': 'Can toggle quiz publish status',
  'quizzes:view-stats': 'Can view quiz statistics',
  'quizzes:sync': 'Can sync quiz questions',
  'quizzes:manage-questions': 'Can add/update/delete quiz questions',

  'packages:read': 'Can view all packages (admin view)',
  'packages:create': 'Can create new packages',
  'packages:update': 'Can update packages',
  'packages:delete': 'Can delete packages',
  'packages:view-stats': 'Can view package statistics',

  'subscriptions:read': 'Can view all subscription plans',
  'subscriptions:create': 'Can create new subscription plans',
  'subscriptions:update': 'Can update subscription plans',
  'subscriptions:delete': 'Can delete subscription plans',

  'settings:read': 'Can view general settings',
  'settings:update': 'Can update general settings',
  'content:update-general': 'Can update general content sections',
  'content:manage-faq': 'Can manage FAQs',
  'content:manage-slider': 'Can manage homepage slider',
  'content:manage-testimonials': 'Can manage testimonials',
  'content:toggle-maintenance': 'Can toggle maintenance mode',
  'content:view-analytics': 'Can view content analytics',

  'social-links:read': 'Can view all social links',
  'social-links:create': 'Can create new social links',
  'social-links:update': 'Can update social links',
  'social-links:delete': 'Can delete social links',

  'over-usage:read': 'Can view usage statistics',
  'over-usage:review': 'Can review suspicious enrollments',
  'over-usage:resolve': 'Can resolve usage alerts',
  'over-usage:bulk-check': 'Can run bulk checks',

  'videos:view-logs': 'Can view video access logs',
  'videos:view-suspicious': 'Can view suspicious video activities',

  'upload:create': 'Can upload files (images, attachments)',
  'upload:delete': 'Can delete uploaded files',

  'campaigns:read': 'Can view all campaigns',
  'campaigns:create': 'Can create campaigns',
  'campaigns:update': 'Can update campaigns',
  'campaigns:delete': 'Can delete campaigns',
  'campaigns:send': 'Can send campaigns',
  'campaigns:schedule': 'Can schedule campaigns',
  'campaigns:cancel': 'Can cancel scheduled campaigns',
  'campaigns:view-analytics': 'Can view campaign analytics',

  'devices:read-all': 'Can view all devices across platform',
  'devices:block': 'Can block any device',
  'devices:unblock': 'Can unblock any device',
  'devices:reset-all': 'Can permanently delete all device records',

  'analytics:read': 'Can view analytics and reports',
  'analytics:export': 'Can export analytics data',

  'notifications:read': 'Can view notifications',
  'notifications:manage': 'Can manage notifications',

  'discussions:read': 'Can view discussions',
  'discussions:moderate': 'Can moderate discussions',
  'discussions:delete': 'Can delete discussions',

  'admin:read': 'Can view admin users and their permissions',
  'admin:create': 'Can create new admin users',
  'admin:update-permissions': 'Can update admin permissions',
  'admin:delete': 'Can delete admin users',
};

export const getPermissionLabel = (permission: string): string => {
  return PERMISSION_LABELS[permission] || permission;
};

export const getPermissionDescription = (permission: string): string => {
  return PERMISSION_DESCRIPTIONS[permission] || '';
};

export const getPermissionGroup = (permission: string): PermissionGroup | undefined => {
  for (const group of PERMISSION_GROUPS) {
    if (group.permissions.includes(permission)) {
      return group;
    }
  }
  return undefined;
};

export const getPermissionsByCategory = (): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  PERMISSION_GROUPS.forEach((group) => {
    map.set(group.id, group.permissions);
  });
  return map;
};

export const hasPermissionInCategory = (
  userPermissions: string[],
  categoryId: string
): boolean => {
  const group = PERMISSION_GROUPS.find((g) => g.id === categoryId);
  if (!group) return false;
  return group.permissions.some((perm) => userPermissions.includes(perm));
};

export const getCategoryPermissionCount = (
  userPermissions: string[],
  categoryId: string
): number => {
  const group = PERMISSION_GROUPS.find((g) => g.id === categoryId);
  if (!group) return 0;
  return group.permissions.filter((perm) => userPermissions.includes(perm)).length;
};

export const getAllPermissions = (): string[] => {
  return PERMISSION_GROUPS.flatMap((group) => group.permissions);
};

// NOTE: Many permissions are defined but not yet implemented in UI.
// These are kept for type safety and future implementation.
// See RBAC audit for full list of unused permissions.

